import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore } from '../../stores/languageStore';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAnalytics, useComponentAnalytics } from '../../hooks/useAnalytics';

interface Lawsuit {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  amount: number;
  currency: string;
  country: string;
  isClassAction: boolean;
  participantCount: number;
  minParticipants: number;
  creator: {
    id: string;
    name: string;
  };
  createdAt: string;
  hasJoined: boolean;
  strengthScore?: number;
  aiAnalysis?: {
    estimatedClaimAmount?: {
      currency: string;
      amount: number;
    };
  };
}

interface FilterOptions {
  category: string;
  country: string;
  status: string;
  isClassAction?: boolean;
  minAmount?: number;
  maxAmount?: number;
}

const BrowseLawsuitsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { isRTL } = useLanguageStore();
  const analytics = useAnalytics();
  
  // Track component usage
  useComponentAnalytics('BrowseLawsuitsPage');
  const queryClient = useQueryClient();
  
  // Track initial browse action
  React.useEffect(() => {
    analytics.trackLawsuit.browse(filters);
  }, []);

  const [filters, setFilters] = useState<FilterOptions>({
    category: '',
    country: '',
    status: '',
    isClassAction: undefined,
    minAmount: undefined,
    maxAmount: undefined,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch lawsuits
  const { data: lawsuitsData, isLoading } = useQuery({
    queryKey: ['lawsuits', filters, searchQuery, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (filters.category) params.append('category', filters.category);
      if (filters.country) params.append('country', filters.country);
      if (filters.status) params.append('status', filters.status);
      if (filters.isClassAction !== undefined) params.append('isClassAction', filters.isClassAction.toString());
      if (filters.minAmount) params.append('minAmount', filters.minAmount.toString());
      if (filters.maxAmount) params.append('maxAmount', filters.maxAmount.toString());
      params.append('sort', `${sortOrder === 'desc' ? '-' : ''}${sortBy}`);

      const response = await axios.get(`/api/lawsuits?${params.toString()}`);
      return response.data;
    },
    enabled: !!user,
  });

  // Join lawsuit mutation
  const joinLawsuitMutation = useMutation({
    mutationFn: async (lawsuitId: string) => {
      const response = await axios.post(`/api/lawsuits/${lawsuitId}/join`);
      return response.data;
    },
    onSuccess: (_data, lawsuitId) => {
      // Track successful lawsuit join
      const lawsuit = lawsuitsData?.data?.find((l: Lawsuit) => l.id === lawsuitId);
      analytics.trackLawsuit.join(lawsuitId, lawsuit?.category);
      queryClient.invalidateQueries({ queryKey: ['lawsuits'] });
    },
    onError: (_error, _lawsuitId) => {
      // Track failed lawsuit join
      analytics.trackError(new Error('Failed to join lawsuit'), 'join_lawsuit_error', 'lawsuit');
    },
  });

  // Leave lawsuit mutation
  const leaveLawsuitMutation = useMutation({
    mutationFn: async (lawsuitId: string) => {
      const response = await axios.post(`/api/lawsuits/${lawsuitId}/leave`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lawsuits'] });
    },
  });

  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      country: '',
      status: '',
      isClassAction: undefined,
      minAmount: undefined,
      maxAmount: undefined,
    });
    setSearchQuery('');
  };

  const handleJoinLawsuit = (lawsuitId: string) => {
    joinLawsuitMutation.mutate(lawsuitId);
  };

  const handleLeaveLawsuit = (lawsuitId: string) => {
    leaveLawsuitMutation.mutate(lawsuitId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'settled':
        return 'bg-green-100 text-green-800';
      case 'won':
        return 'bg-green-100 text-green-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const categories = [
    'consumer_rights',
    'employment',
    'personal_injury',
    'contract_dispute',
    'property',
    'family',
    'criminal',
    'other'
  ];

  const countries = ['US', 'UK', 'IL', 'UAE', 'DE', 'FR'];
  const statuses = ['submitted', 'in_progress', 'settled', 'won', 'lost'];

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('navigation.browseLawsuits')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('browseLawsuits.subtitle')}
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="space-y-6">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.search')}
              </label>
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('browseLawsuits.searchPlaceholder')}
              />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('lawsuit.category')}
                </label>
                <select
                  id="category"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('browseLawsuits.allCategories')}</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {t(`lawsuit.categories.${category}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('lawsuit.country')}
                </label>
                <select
                  id="country"
                  value={filters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('browseLawsuits.allCountries')}</option>
                  {countries.map(country => (
                    <option key={country} value={country}>
                      {t(`countries.${country}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('lawsuit.status')}
                </label>
                <select
                  id="status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('browseLawsuits.allStatuses')}</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>
                      {t(`lawsuit.statuses.${status}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('browseLawsuits.type')}
                </label>
                <select
                  id="type"
                  value={filters.isClassAction === undefined ? '' : filters.isClassAction.toString()}
                  onChange={(e) => {
                    const value = e.target.value === '' ? undefined : e.target.value === 'true';
                    handleFilterChange('isClassAction', value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('browseLawsuits.allTypes')}</option>
                  <option value="true">{t('browseLawsuits.classActionOnly')}</option>
                  <option value="false">{t('browseLawsuits.individualOnly')}</option>
                </select>
              </div>
            </div>

            {/* Amount Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="minAmount" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('browseLawsuits.minAmount')}
                </label>
                <input
                  type="number"
                  id="minAmount"
                  value={filters.minAmount || ''}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label htmlFor="maxAmount" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('browseLawsuits.maxAmount')}
                </label>
                <input
                  type="number"
                  id="maxAmount"
                  value={filters.maxAmount || ''}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="∞"
                />
              </div>
            </div>

            {/* Sort and Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div>
                  <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.sort')}
                  </label>
                  <select
                    id="sort"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="createdAt">{t('browseLawsuits.sortByDate')}</option>
                    <option value="amount">{t('browseLawsuits.sortByAmount')}</option>
                    <option value="participantCount">{t('browseLawsuits.sortByParticipants')}</option>
                    <option value="strengthScore">{t('browseLawsuits.sortByStrength')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('browseLawsuits.order')}
                  </label>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>

              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('common.clear')}
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : lawsuitsData?.data?.length > 0 ? (
          <div className="space-y-6">
            <div className="text-sm text-gray-600 mb-4">
              {t('browseLawsuits.resultsCount', { count: lawsuitsData?.pagination?.total || 0 })}
            </div>

            {lawsuitsData.data.map((lawsuit: Lawsuit) => (
              <div key={lawsuit.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 
                          className="text-lg font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                          onClick={() => {
                            analytics.trackLawsuit.viewDetails(lawsuit.id, lawsuit.category);
                            navigate(`/lawsuit/${lawsuit.id}`);
                          }}
                        >
                          {lawsuit.title}
                        </h3>
                        {lawsuit.isClassAction && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {t('lawsuit.isClassAction')}
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lawsuit.status)}`}>
                          {t(`lawsuit.statuses.${lawsuit.status}`)}
                        </span>
                      </div>

                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {lawsuit.description}
                      </p>

                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <span>{t(`lawsuit.categories.${lawsuit.category}`)}</span>
                        <span>{t(`countries.${lawsuit.country}`)}</span>
                        <span>{new Date(lawsuit.createdAt).toLocaleDateString()}</span>
                        {lawsuit.isClassAction && (
                          <span>
                            {lawsuit.participantCount} / {lawsuit.minParticipants} {t('browseLawsuits.participants')}
                          </span>
                        )}
                        {lawsuit.strengthScore && (
                          <span className="flex items-center">
                            <svg className="w-4 h-4 text-yellow-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            {lawsuit.strengthScore}/100
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          {(
                            (lawsuit.currency || lawsuit.aiAnalysis?.estimatedClaimAmount?.currency || '') + ' ' +
                            (typeof lawsuit.amount === 'number'
                              ? lawsuit.amount.toLocaleString()
                              : (typeof lawsuit.aiAnalysis?.estimatedClaimAmount?.amount === 'number'
                                ? lawsuit.aiAnalysis.estimatedClaimAmount.amount.toLocaleString()
                                : t('browseLawsuits.noAmount')))
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {t('browseLawsuits.claimAmount')}
                        </div>
                      </div>

                      <div className="flex flex-col space-y-2">
                        <button
                          onClick={() => {
                            analytics.trackLawsuit.viewDetails(lawsuit.id, lawsuit.category);
                            navigate(`/lawsuit/${lawsuit.id}`);
                          }}
                          className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                        >
                          {t('common.view')}
                        </button>

                        {lawsuit.isClassAction && (
                          lawsuit.hasJoined ? (
                            <button
                              onClick={() => handleLeaveLawsuit(lawsuit.id)}
                              disabled={leaveLawsuitMutation.isPending}
                              className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50"
                            >
                              {leaveLawsuitMutation.isPending ? t('common.loading') : t('lawsuit.leaveLawsuit')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleJoinLawsuit(lawsuit.id)}
                              disabled={joinLawsuitMutation.isPending}
                              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                            >
                              {joinLawsuitMutation.isPending ? t('common.loading') : t('lawsuit.joinLawsuit')}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m-46-4v20c0 4.418 7.163 8 16 8 1.867 0 3.676-.149 5.395-.42" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('browseLawsuits.noResults')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('browseLawsuits.noResultsDesc')}
            </p>
            <div className="mt-6">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                {t('browseLawsuits.clearFilters')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseLawsuitsPage; 